import React, { useEffect, useMemo, useState } from "react";
import { IconDownload } from "../components/Icons";
import { Card, Empty, PageHeader, Stat, Tag } from "../components/ui";
import { api, safeGet } from "../lib/api";
import { fmtDate, isNum, pct } from "../lib/format";

// ---------- helpers ----------

function parseCSV(text) {
  const lines = String(text || "").split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const head = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map((l) => {
    const cells = l.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
    const o = {};
    head.forEach((h, i) => {
      const v = cells[i];
      const n = Number(v);
      o[h] = v === "" || v == null ? null : Number.isFinite(n) && v !== "" ? n : v;
    });
    return o;
  });
}

function pick(obj, keys, fallback = null) {
  for (const k of keys) {
    if (obj && obj[k] != null) return obj[k];
  }
  return fallback;
}

function asPct(v) {
  if (!isNum(v)) return null;
  return Math.abs(v) <= 1.5 && Math.abs(v) !== 0 ? v * 100 : v;
}

// Metrics object may be nested as {portfolio:{cagr..}, benchmark:{...}} or flat keys.
function metric(summary, side, names) {
  const nested = summary?.[side] || summary?.[`${side}_metrics`] || summary?.metrics?.[side];
  if (nested) {
    const v = pick(nested, names);
    if (v != null) return v;
  }
  const flat = pick(summary, names.map((n) => `${side}_${n}`).concat(names.map((n) => `${n}_${side}`)));
  return flat;
}

function seriesFromRows(rows) {
  if (!rows.length) return { dates: [], portfolio: [], benchmark: [] };
  const keys = Object.keys(rows[0]);
  const dateKey = keys.find((k) => /date|period|as_of|time/i.test(k)) || keys[0];
  const pKey = keys.find((k) => /^(portfolio|strategy|equity|port)/i.test(k)) || keys.find((k) => k !== dateKey && isNum(rows[0][k]));
  const bKey = keys.find((k) => /bench|index|spy|nsei|hsi/i.test(k) && k !== pKey);
  return {
    dates: rows.map((r) => r[dateKey]),
    portfolio: rows.map((r) => (isNum(r[pKey]) ? r[pKey] : null)),
    benchmark: bKey ? rows.map((r) => (isNum(r[bKey]) ? r[bKey] : null)) : [],
    pKey,
    bKey,
  };
}

function LineChart({ dates, series, height = 260 }) {
  const W = 800;
  const H = height;
  const padL = 46;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const all = series.flatMap((s) => s.values.filter(isNum));
  if (!all.length) return <Empty>No equity data</Empty>;
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;
  const n = dates.length;
  const x = (i) => padL + (i / Math.max(1, n - 1)) * (W - padL - padR);
  const y = (v) => padT + (1 - (v - min) / span) * (H - padT - padB);
  const path = (vals) => {
    let d = "";
    vals.forEach((v, i) => {
      if (!isNum(v)) return;
      d += `${d ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
    });
    return d;
  };
  const ticks = 4;
  const yt = Array.from({ length: ticks + 1 }, (_, i) => min + (span * i) / ticks);
  const xt = [0, Math.floor(n / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i);
  const fmtV = (v) => (Math.abs(max) > 50 ? v.toFixed(0) : v.toFixed(2));
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Equity curve" style={{ display: "block" }}>
      {yt.map((v) => (
        <g key={v}>
          <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeWidth="1" />
          <text x={padL - 6} y={y(v) + 4} textAnchor="end" fontSize="10" fill="var(--muted)" fontFamily="var(--font-mono)">
            {fmtV(v)}
          </text>
        </g>
      ))}
      {xt.map((i) => (
        <text key={i} x={x(i)} y={H - 8} textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"} fontSize="10" fill="var(--muted)" fontFamily="var(--font-mono)">
          {String(dates[i] ?? "").slice(0, 10)}
        </text>
      ))}
      {series.map((s) => (
        <path key={s.name} d={path(s.values)} fill="none" stroke={s.color} strokeWidth={s.width || 2} strokeLinejoin="round" strokeLinecap="round" strokeDasharray={s.dash} />
      ))}
    </svg>
  );
}

// ---------- page ----------

export default function Backtest() {
  const [summary, setSummary] = useState(null);
  const [files, setFiles] = useState([]);
  const [equity, setEquity] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, f] = await Promise.all([safeGet("/api/backtest/latest", {}), safeGet("/api/backtest/files", [])]);
      const sum = s && typeof s === "object" && Object.keys(s).length ? s : null;
      const list = Array.isArray(f) ? f : [];
      setSummary(sum);
      setFiles(list);
      setLoaded(true);
      const market = String(sum?.market || "").toUpperCase();
      const candidates = list
        .filter((x) => /_equity\.csv$/i.test(x.name) && (!market || x.name.toUpperCase().includes(market)))
        .sort((a, b) => String(b.modified || "").localeCompare(String(a.modified || "")) || b.name.localeCompare(a.name));
      const pickFile = candidates[0] || list.filter((x) => /_equity\.csv$/i.test(x.name))[0];
      if (pickFile) {
        try {
          const { data } = await api.get(`/api/backtest/file/${encodeURIComponent(pickFile.name)}`, { responseType: "text", transformResponse: [(d) => d] });
          setEquity({ name: pickFile.name, ...seriesFromRows(parseCSV(data)) });
        } catch (e) {
          setEquity(null);
        }
      }
    })();
  }, []);

  const m = useMemo(() => {
    if (!summary) return null;
    const get = (side) => ({
      cagr: asPct(metric(summary, side, ["cagr", "cagr_pct", "CAGR"])),
      mdd: asPct(metric(summary, side, ["max_drawdown", "mdd", "max_drawdown_pct", "MDD"])),
      win: asPct(metric(summary, side, ["win_rate", "win_rate_pct", "hit_rate"])),
      total: asPct(metric(summary, side, ["total_return", "total_return_pct", "return"])),
    });
    return { p: get("portfolio"), b: get("benchmark") };
  }, [summary]);

  const periods = useMemo(() => {
    const arr = summary?.periods || summary?.period_results || summary?.results || [];
    return Array.isArray(arr) ? arr : [];
  }, [summary]);
  const limitations = useMemo(() => {
    const l = summary?.limitations || summary?.caveats || summary?.notes || [];
    return Array.isArray(l) ? l : l ? [String(l)] : [];
  }, [summary]);

  const window = summary
    ? summary.window || [summary.start || summary.from, summary.end || summary.to].filter(Boolean).join(" → ")
    : null;
  const nPeriods = isNum(summary?.n_periods) ? summary.n_periods : periods.length || summary?.periods_count;
  const topN = summary?.top_n || summary?.topN;

  const sub = summary
    ? `${summary.market || "—"}${topN ? ` top-${topN}` : ""} · ${window || "—"} · ${nPeriods || 0} periods${summary.generated_at || summary.as_of ? ` · ${fmtDate(summary.generated_at || summary.as_of, { time: false })}` : ""}`
    : loaded
      ? "No backtest summary found — run backtest.py to generate one"
      : "Loading…";

  const perColor = (v, invert) => (isNum(v) ? ((invert ? v <= 0 : v >= 0) ? "var(--good)" : "var(--bad)") : undefined);
  const periodKeys = periods.length ? Object.keys(periods[0]).slice(0, 8) : [];

  return (
    <>
      <PageHeader title="Backtest" sub={sub}>
        {files.length > 0 && (
          <a className="btn" href={`/api/backtest/file/${encodeURIComponent(files[0].name)}`} download>
            <IconDownload /> Download files
          </a>
        )}
      </PageHeader>

      {!summary && loaded && (
        <div className="card">
          <Empty title="No backtest yet">Results appear here once a *_summary.json exists in the backtest folder.</Empty>
        </div>
      )}

      {summary && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))" }}>
            <Stat label="CAGR · portfolio" value={pct(m.p.cagr, { digits: 1 })} color={perColor(m.p.cagr)} sub={isNum(m.p.total) ? `total ${pct(m.p.total, { digits: 1 })}` : undefined} />
            <Stat label="CAGR · benchmark" value={pct(m.b.cagr, { digits: 1 })} color={perColor(m.b.cagr)} sub={isNum(m.b.total) ? `total ${pct(m.b.total, { digits: 1 })}` : undefined} />
            <Stat label="Max drawdown · portfolio" value={pct(m.p.mdd, { sign: false, digits: 1 })} color={isNum(m.p.mdd) ? "var(--bad)" : undefined} />
            <Stat label="Max drawdown · benchmark" value={pct(m.b.mdd, { sign: false, digits: 1 })} color={isNum(m.b.mdd) ? "var(--bad)" : undefined} />
            <Stat label="Win rate · portfolio" value={pct(m.p.win, { sign: false, digits: 0 })} sub="periods beating 0%" />
            <Stat label="Win rate · benchmark" value={pct(m.b.win, { sign: false, digits: 0 })} sub="periods beating 0%" />
          </div>

          <Card id="bt-eq" title="Equity curve" desc={equity ? `${equity.name} · ${equity.dates.length} points` : "No *_equity.csv found for this market"}>
            {equity ? (
              <>
                <LineChart
                  dates={equity.dates}
                  series={[
                    { name: "Portfolio", values: equity.portfolio, color: "var(--accent)", width: 2.2 },
                    ...(equity.benchmark.length ? [{ name: "Benchmark", values: equity.benchmark, color: "var(--muted)", width: 1.5, dash: "4 4" }] : []),
                  ]}
                />
                <div style={{ display: "flex", gap: 16, fontSize: 12, color: "var(--muted)" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 16, height: 2, background: "var(--accent)" }} />Portfolio{equity.pKey ? ` (${equity.pKey})` : ""}</span>
                  {equity.benchmark.length > 0 && (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 16, borderTop: "2px dashed var(--muted)" }} />Benchmark{equity.bKey ? ` (${equity.bKey})` : ""}</span>
                  )}
                </div>
              </>
            ) : (
              <Empty>Equity curve unavailable.</Empty>
            )}
          </Card>

          <div className="table-wrap">
            <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", fontSize: 13, fontWeight: 600 }}>Periods</div>
            <div className="table-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>#</th>
                    {periodKeys.map((k) => (
                      <th key={k}>{k.replace(/_/g, " ")}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {periods.map((p, i) => (
                    <tr key={i}>
                      <td className="num" style={{ color: "var(--muted)" }}>{i + 1}</td>
                      {periodKeys.map((k) => {
                        const v = p[k];
                        const isP = /pct|return|cagr|drawdown|ret/i.test(k) && isNum(v);
                        return (
                          <td key={k} className="num" style={isP ? { color: v >= 0 ? "var(--good)" : "var(--bad)" } : undefined}>
                            {isP ? pct(asPct(v), { digits: 1 }) : Array.isArray(v) ? v.join(", ") : isNum(v) ? v.toLocaleString("en-US", { maximumFractionDigits: 2 }) : String(v ?? "—")}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!periods.length && <Empty>No per-period results in the summary.</Empty>}
          </div>

          <div className="logs-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16 }}>
            <Card id="bt-lim" title="Limitations" desc="Read before trusting any number above">
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: "var(--muted)" }}>
                {limitations.map((l, i) => (
                  <li key={i}>{String(l)}</li>
                ))}
                <li>Fundamentals are point-in-time only to the extent the statement-lag guard allows; survivorship bias in today's index membership is not corrected.</li>
                <li>No transaction costs, taxes or slippage. Equal-weight rebalance assumes fills at the close.</li>
                <li>Past performance is not indicative of future results. This is not investment advice.</li>
              </ul>
            </Card>
            <Card id="bt-files" title="Files" desc={files.length ? `${files.length} files in the backtest folder` : "No files"}>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6, maxHeight: 260, overflow: "auto" }}>
                {files.map((f) => (
                  <li key={f.name} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12 }}>
                    <a href={`/api/backtest/file/${encodeURIComponent(f.name)}`} download style={{ textDecoration: "none", color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</a>
                    <span className="num" style={{ color: "var(--muted)", whiteSpace: "nowrap" }}>
                      {isNum(f.size) ? `${(f.size / 1024).toFixed(0)} KB` : ""}
                      {f.modified ? ` · ${fmtDate(f.modified, { time: false })}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
              {summary?.benchmark_ticker && <Tag kind="neutral">benchmark {summary.benchmark_ticker}</Tag>}
            </Card>
          </div>
        </>
      )}
    </>
  );
}
